import { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  DatePicker,
  Form,
  Input,
  Card, Table, InputNumber, Popconfirm, message, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import { any, bool, string } from 'prop-types';
import moment from 'moment';
import { addDecimalNum } from '@/utils/utils';
import { uploadFileUrl } from '@/services/majorFinancing';
import FileDownload from '@/components/FileDownload/FileDownload';

const formItemLayout = {
  labelCol: {
    sm: { span: 6 },
  },
  wrapperCol: {
    sm: { span: 18 },
  },
};

// eslint-disable-next-line no-loss-of-precision
const maxLength = 99999999999999999999;

export default function AddList(props) {
  const {
    formRef, title, name, isEdit, enterpriseName, dataFieldName, initialData,
  } = props;
  const [open, setOpen] = useState(3);
  const [dataSource, setDataSource] = useState([]);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const type = title === '订单';

  const uploadProps = {
    name: 'file',
    accept: '.rar,.zip,.doc,.docx,.pdf,.jpg,.jpeg,.png',
    action: uploadFileUrl,
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file) {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('请上传10M以下文件!');
      }
      return isLt10M;
    },
    onChange({ file, fileList: newFileList }) {
      setFileList(newFileList);
      if (file.status === 'done') {
        message.success('上传成功');
      } else if (file.status === 'error') {
        message.error('上传失败');
      }
    },
  };

  const onCancel = () => {
    setOpen(3);
    form.resetFields();
    setFileList([]);
  };
  // 数据排序函数：优先按年份倒序，同年份按季度倒序
  const sortData = (data) => data.sort((a, b) => {
    // 先按年份倒序
    if (b.year !== a.year) {
      return b.year - a.year;
    }
    // 年份相同，按季度倒序
    return b.quarter - a.quarter;
  }).map((item, index) => ({
    ...item,
    key: index + 1,
  }));

  // 处理初始数据回显
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      const sortedData = sortData(initialData);
      setDataSource(sortedData);
      const amountField = type ? 'orderAmount' : 'invoiceAmount';
      formRef.setFieldsValue({ [name]: addDecimalNum(sortedData, amountField) });
      if (dataFieldName) {
        formRef.setFieldsValue({ [dataFieldName]: sortedData });
      }
    }
  }, [initialData]);

  const onOk = () => {
    form.validateFields().then((values) => {
      const obj = { ...values };
      obj.year = values.quarter.year();
      obj.quarter = values.quarter.quarter();
      obj.key = values.key ?? dataSource.length + 1;
      // 处理附件
      if (values.files?.fileList) {
        obj.files = values.files.fileList.map((item) => ({
          fileName: item.name,
          fileUrl: item.response?.data || item.url,
        }));
      }
      let newData = [];
      newData = values.key ? dataSource.map((item) => (item.key === values.key ? { ...obj } : item)) : [...dataSource, { ...obj }];
      // 排序数据
      const sortedData = sortData(newData);
      setDataSource(sortedData);
      const amountField = type ? 'orderAmount' : 'invoiceAmount';
      formRef.setFieldsValue({ [name]: addDecimalNum(sortedData, amountField) });
      if (dataFieldName) {
        formRef.setFieldsValue({ [dataFieldName]: sortedData });
      }
      onCancel();
    });
  };

  const del = (id) => {
    const filteredList = dataSource.filter((item) => item.key !== id);
    // 删除后重新排序
    const sortedList = sortData(filteredList);
    setDataSource(sortedList);
    const amountField = type ? 'orderAmount' : 'invoiceAmount';
    formRef.setFieldsValue({ [name]: addDecimalNum(sortedList, amountField) });
    if (dataFieldName) {
      formRef.setFieldsValue({ [dataFieldName]: sortedList });
    }
    message.success('删除成功');
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'key',
      width: 60,
    },
    {
      title: '年度',
      dataIndex: 'year',
      width: 100,
    },
    {
      title: '季度',
      dataIndex: 'quarter',
      width: 100,
    },
    {
      title: '上/下游企业名称',
      width: 300,
      dataIndex: 'relatedApplicantName',
    },
    {
      title: '商品/服务名称',
      width: 150,
      dataIndex: 'productName',
    },
    type ? null : {
      title: '规格',
      width: 100,
      dataIndex: 'specification',
    },
    {
      title: '数量',
      width: 100,
      dataIndex: 'num',
    },
    {
      title: '单价(元)',
      width: 100,
      dataIndex: 'unitPrice',
    },
    {
      title: `${title}总金额(元)`,
      width: 150,
      dataIndex: type ? 'orderAmount' : 'invoiceAmount',
    },
    {
      title: '备注',
      width: 200,
      dataIndex: 'remark',
    },
    {
      title: '附件材料',
      dataIndex: 'files',
      width: 250,
      render: (attachments) => {
        if (!attachments || attachments.length === 0) return '-';
        return (
          <div>
            {attachments.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <FileDownload key={index} appendixName={item.fileName} fileUrl={item.fileUrl} />
            ))}
          </div>
        );
      },
    },
    isEdit
      ? {
        title: '操作',
        width: 100,
        render: (_, record) => (
          <>
            <a
              onClick={() => {
                setOpen(2);
                // 处理编辑时的附件回显
                const editFileList = record.files ? record.files.map((file, index) => ({
                  uid: index.toString(),
                  name: file.fileName,
                  status: 'done',
                  url: file.fileUrl,
                })) : [];
                setFileList(editFileList);
                form.setFieldsValue({
                  ...record,
                  quarter: moment().year(record.year).quarter(record.quarter),
                  files: editFileList.length > 0 ? { fileList: editFileList } : undefined,
                });
              }}
              style={{ marginRight: 10 }}
            >
              编辑
            </a>
            <Popconfirm
              title="确定删除当前选项吗?"
              onConfirm={() => del(record.key)}
              okText="确定"
              cancelText="删除"
            >
              <a href="#">删除</a>
            </Popconfirm>
          </>
        ),
      } : null,
  ];

  return (
    <Card title={`${title}信息`}>
      <Form.Item
        required
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 20 }}
        label={`${title}总金额`}
      >
        <div style={{ display: 'flex' }}>
          <Form.Item
            name={name}
            rules={[
              {
                required: true,
                validator: () => {
                  if (isEmpty(dataSource)) {
                    return Promise.reject(new Error(`请添加${title}信息`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber placeholder="请输入" min={0} max={maxLength} addonAfter="元" disabled />
          </Form.Item>
          <Button
            hidden={!isEdit}
            style={{ marginLeft: 20 }}
            type="primary"
            onClick={() => {
              setOpen(1);
              if (enterpriseName) {
                form.setFieldsValue({ relatedApplicantName: enterpriseName });
              }
            }}
          >
            添加
          </Button>
        </div>
      </Form.Item>
      <Table size="small" columns={columns.filter((item) => item !== null)} dataSource={dataSource} pagination={false} scroll={{ y: 500, x: 'max-content' }} />
      <Modal
        title={`${title}信息${open === 1 ? '添加' : '编辑'}`}
        maskClosable={false}
        destroyOnClose
        open={open !== 3}
        onOk={onOk}
        onCancel={onCancel}
      >
        <Form form={form} {...formItemLayout}>
          <Form.Item hidden name="key">
            <Input />
          </Form.Item>
          {/* <Form.Item */}
          {/*  name="year" */}
          {/*  label="年度" */}
          {/*  rules={[ */}
          {/*    { */}
          {/*      required: true, */}
          {/*      message: '请选择年度', */}
          {/*    }, */}
          {/*  ]} */}
          {/* > */}
          {/*  <DatePicker picker="year" style={{ width: '100%' }} /> */}
          {/* </Form.Item> */}
          <Form.Item
            name="quarter"
            label="季度"
            rules={[
              {
                required: true,
                message: '请选择季度',
              },
            ]}
          >
            <DatePicker picker="quarter" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="relatedApplicantName"
            label="上/下游企业名称"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input placeholder={enterpriseName || '上/下游企业名称'} maxLength={100} disabled={!!enterpriseName} />
          </Form.Item>
          <Form.Item
            name="productName"
            label="商品/服务名称"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input placeholder="请输入商品/服务名称" maxLength={100} />
          </Form.Item>
          {
            !type && (
              <Form.Item
                name="specification"
                label="规格"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input placeholder="请输入规格" maxLength={100} />
              </Form.Item>
            )
          }
          <Form.Item
            name="unitPrice"
            label="单价"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber placeholder="请输入单价" min={0} max={maxLength} addonAfter="元" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="num"
            label="数量"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber placeholder="请输入数量" min={0} max={maxLength} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name={type ? 'orderAmount' : 'invoiceAmount'}
            label={`${title}总金额`}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber placeholder="请输入总金额" min={0} addonAfter="元" max={maxLength} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea maxLength={1000} showCount placeholder="请输入备注" />
          </Form.Item>
          <Form.Item
            name="files"
            label="附件材料"
            valuePropName="file"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList ? { fileList: e.fileList } : undefined;
            }}
          >
            <Upload {...uploadProps} fileList={fileList}>
              <Button icon={<UploadOutlined />}>上传附件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

AddList.propTypes = {
  title: String,
  formRef: any,
  name: String,
  isEdit: bool,
  enterpriseName: string,
  dataFieldName: string,
  initialData: any,
};
AddList.defaultProps = {
  isEdit: true,
  enterpriseName: '',
  dataFieldName: undefined,
  initialData: undefined,
};
